/**
 * Free gift with purchase
 * - Reads settings from data attributes rendered by snippets/free-gift-gauge.liquid
 * - Auto adds / removes the gift variant from the cart
 * - Updates the progress gauge on every theme:cart:change event
 *
 * Requires: an Automatic Discount configured in Shopify Admin
 * (Discounts > Automatic discount > 100% off the gift product)
 * covering the same date range as set here. This script only
 * controls WHEN the gift is in the cart, not its price.
 */
(function () {
  'use strict';

  var routes = (window.theme && window.theme.routes) || { cart: '/cart' };
  var isSyncing = false; // guards against reacting to our own cart mutation

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    try {
      var currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
        (window.theme && window.theme.currencyCode) ||
        'EUR';
      var locale = document.documentElement.getAttribute('lang') || 'fr-FR';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: amount.endsWith('.00') ? 0 : 2
      }).format(cents / 100);
    } catch (e) {
      return amount + ' €';
    }
  }

  function todayStr() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  function withinDateRange(start, end) {
    var today = todayStr();
    if (start && today < start) return false;
    if (end && today > end) return false;
    return true;
  }

  function getGiftEls() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-free-gift]'));
  }

  function getThreshold(el) {
    var raw = el.getAttribute('data-free-gift-threshold');
    var value = parseInt(raw, 10);
    if (isNaN(value) || value <= 0) {
      console.warn('free-gift.js: seuil invalide ("' + raw + '"), vérifie le champ "Seuil d\'achat" dans le Personnalisateur.');
      return null;
    }
    return value;
  }

  function findGiftLineItem(cart, variantId) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(variantId)) {
        return cart.items[i];
      }
    }
    return null;
  }

  function addGiftToCart(variantId) {
    return window
      .fetch(routes.cart + '/add.js', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            console.warn('free-gift.js: ajout du cadeau impossible —', data.message || data.description || data);
            return null;
          }
          return window.fetch(routes.cart + '.js').then(function (r) {
            return r.json();
          });
        });
      });
  }

  function removeGiftFromCart(variantId) {
    return window
      .fetch(routes.cart + '/change.js', {
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 })
      })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            console.warn('free-gift.js: retrait du cadeau impossible —', data.message || data.description || data);
            return null;
          }
          return data;
        });
      });
  }

  function dispatchCartChange(cart) {
    document.dispatchEvent(
      new CustomEvent('theme:cart:change', {
        detail: { cart: cart },
        bubbles: true
      })
    );
  }

  function render(cart) {
    var els = getGiftEls();
    if (!els.length) return;

    els.forEach(function (el) {
      var variantId = el.getAttribute('data-free-gift-variant-id');
      var threshold = getThreshold(el);
      var start = el.getAttribute('data-free-gift-start');
      var end = el.getAttribute('data-free-gift-end');
      var active = withinDateRange(start, end);

      if (!active || !threshold || !variantId) {
        el.hidden = true;
        return;
      }

      // Total excluding the gift line itself, so reaching the threshold
      // isn't artificially inflated by the gift's own (discounted) price.
      var giftLine = findGiftLineItem(cart, variantId);
      var giftLineTotal = giftLine ? giftLine.final_line_price : 0;
      var comparableTotal = cart.total_price - giftLineTotal;
      var remaining = threshold - comparableTotal;
      var percent = Math.max(0, Math.min(100, (comparableTotal / threshold) * 100));
      var isComplete = comparableTotal >= threshold;

      el.hidden = false;
      el.classList.toggle('is-complete', isComplete);

      var bar = el.querySelector('[data-free-gift-bar]');
      if (bar) bar.style.width = percent + '%';

      var msg = el.querySelector('[data-free-gift-message]');
      if (msg) {
        if (isComplete) {
          msg.innerHTML = msg.getAttribute('data-fg-success-text') || '';
        } else {
          var template = msg.getAttribute('data-fg-progress-text') || '';
          msg.innerHTML = template.replace('||amount||', '<strong>' + formatMoney(remaining) + '</strong>');
        }
      }
    });
  }

  function sync(cart) {
    // Only one gauge config drives the add/remove logic (they all share settings
    // in practice, but we use the first enabled one found on the page).
    var el = getGiftEls()[0];
    if (!el || isSyncing) {
      render(cart);
      return;
    }

    var variantId = el.getAttribute('data-free-gift-variant-id');
    var threshold = getThreshold(el);
    var start = el.getAttribute('data-free-gift-start');
    var end = el.getAttribute('data-free-gift-end');
    var active = withinDateRange(start, end);

    if (!active || !threshold || !variantId) {
      render(cart);
      return;
    }

    var giftLine = findGiftLineItem(cart, variantId);
    var giftLineTotal = giftLine ? giftLine.final_line_price : 0;
    var comparableTotal = cart.total_price - giftLineTotal;
    var qualifies = comparableTotal >= threshold;

    if (qualifies && !giftLine) {
      isSyncing = true;
      addGiftToCart(variantId)
        .then(function (freshCart) {
          if (freshCart) dispatchCartChange(freshCart);
        })
        .finally(function () {
          isSyncing = false;
        });
      return;
    }

    if (!qualifies && giftLine) {
      isSyncing = true;
      removeGiftFromCart(variantId)
        .then(function (freshCart) {
          if (freshCart) dispatchCartChange(freshCart);
        })
        .finally(function () {
          isSyncing = false;
        });
      return;
    }

    render(cart);
  }

  document.addEventListener('theme:cart:change', function (e) {
    if (e.detail && e.detail.cart) {
      sync(e.detail.cart);
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    window
      .fetch(routes.cart + '.js')
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        sync(cart);
      })
      .catch(function (err) {
        console.error('free-gift.js:', err);
      });
  });
})();